import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import TopBar from '@/components/TopBar';
import PlaceCard from '@/components/PlaceCard';
import LoadingOverlay from '@/components/LoadingOverlay';
import { CATEGORIES } from '@/components/NeedNowGrid';
import { useCity } from '@/context/CityContext';
import { getPlaces } from '@/lib/api';
import { ArrowLeft, Map as MapIcon, Filter } from 'lucide-react';
import { toast } from 'sonner';

const SORTS = [
  { key: 'distance', label: 'Closest' },
  { key: 'name', label: 'A–Z' },
  { key: 'open', label: '24/7 first' },
];

export default function NeedCategory() {
  const { category } = useParams();
  const navigate = useNavigate();
  const { city } = useCity();
  const meta = CATEGORIES.find((c) => c.key === category);
  const [sort, setSort] = useState('distance');
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let c = true;
    setLoading(true);
    getPlaces(city.lat, city.lon, category, 2500, 25)
      .then((r) => { if (c) setItems(r.items || []); })
      .catch((e) => { if (c) { toast.error('Could not fetch places'); setItems([]); } })
      .finally(() => c && setLoading(false));
    return () => { c = false; };
  }, [city.lat, city.lon, category]);

  const sorted = useMemo(() => {
    if (!items) return [];
    const arr = [...items];
    if (sort === 'distance') arr.sort((a, b) => (a.distance_m ?? Infinity) - (b.distance_m ?? Infinity));
    if (sort === 'name') arr.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    if (sort === 'open') arr.sort((a, b) => (b.open_now === true) - (a.open_now === true));
    return arr;
  }, [items, sort]);

  const Icon = meta?.icon;

  // proper pluralization for English category labels
  const pluralLabel = (label, n) => {
    if (!label) return n === 1 ? 'place' : 'places';
    const lower = label.toLowerCase();
    if (n === 1) return lower;
    if (lower.endsWith('y')) return lower.slice(0, -1) + 'ies';
    if (lower.endsWith('s')) return lower;
    return lower + 's';
  };

  return (
    <div data-testid="need-category-screen">
      <TopBar />
      <div className="px-5 pt-1 flex items-center gap-2">
        <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-[#0F1A13]/05 border border-black/5 flex items-center justify-center hover:bg-[#C4FF3D]/30 puvio-focus" aria-label="Back" data-testid="back-button">
          <ArrowLeft className="w-4 h-4 text-[#0F1A13]" />
        </button>
        <div className="flex-1">
          <p className="text-xs uppercase tracking-wider font-semibold text-[#15291C]">Need now</p>
          <h1 className="text-2xl font-semibold tracking-tight capitalize" data-testid="need-category-title">{meta?.label || category}</h1>
        </div>
        <button onClick={() => navigate(`/map?c=${category}`)} className="puvio-pill puvio-pill-light puvio-focus" data-testid="need-category-open-map">
          <MapIcon className="w-3.5 h-3.5" /> Map
        </button>
      </div>

      {/* hero strip */}
      {Icon && (
        <div className="mx-5 mt-4 rounded-[22px] p-5 bg-[#15291C] puvio-noise relative overflow-hidden text-[#0F1A13]">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-[#C4FF3D]/90 flex items-center justify-center">
              <Icon className="w-6 h-6 text-[#0F1A13]" />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wider text-[#15291C]">Nearby in {city.name}</p>
              <h2 className="text-lg font-semibold">{sorted.length} {pluralLabel(meta?.label, sorted.length)} nearby</h2>
            </div>
          </div>
        </div>
      )}

      <div className="px-5 pt-4 flex items-center gap-2 overflow-x-auto no-scrollbar">
        <Filter className="w-4 h-4 text-[#4A5A50] shrink-0" />
        {SORTS.map((s) => (
          <button
            key={s.key}
            type="button"
            onClick={() => setSort(s.key)}
            className={`puvio-pill puvio-focus ${sort === s.key ? 'puvio-pill-accent' : 'puvio-pill-light'}`}
            data-testid={`sort-${s.key}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="px-5 pt-4 flex flex-col gap-3">
        {loading && <LoadingOverlay label={`Finding nearby ${meta?.label?.toLowerCase() || category}…`} />}
        {!loading && sorted.length === 0 && (
          <div className="puvio-card p-8 text-center">
            <p className="text-sm text-[#4A5A50]">No places found within 2.5km.</p>
            <button onClick={() => navigate('/map')} className="mt-4 puvio-pill puvio-pill-accent puvio-focus">Try map view</button>
          </div>
        )}
        {!loading && sorted.map((p, i) => (
          <PlaceCard key={p.id} place={p} origin={city} index={i} />
        ))}
      </div>
    </div>
  );
}
